import { ReactNode } from "react";
import { Link } from "react-router-dom";
import { ArrowRight, Check } from "lucide-react";
import { Button } from "@/components/ui/button";
import { renderDisplayText } from "@/lib/utils";
import { TenantImage } from "@/components/home/TenantImage";
import cleanLivingRoom from "@/assets/clean-living-room.jpg";

interface CTAPanelProps {
  eyebrow?: string;
  title: string;
  subtitle: ReactNode;
  buttonText: string;
  buttonHref: string;
  secondary?: ReactNode;
  checklist?: string[];
  image?: string;
}

/** Inset, brand-coloured call-to-action panel shared by the tenant pages. */
export function CTAPanel({ eyebrow, title, subtitle, buttonText, buttonHref, secondary, checklist = [], image = cleanLivingRoom }: CTAPanelProps) {
  return (
    <section className="tp-section--tight">
      <div className="container mx-auto px-5 md:px-6">
        <div className="tp-cta-panel grid lg:grid-cols-12" data-reveal>
          <div className="p-8 sm:p-12 lg:col-span-7 lg:p-16">
            {eyebrow && <p className="tp-eyebrow mb-6">{eyebrow}</p>}
            <h2
              className={
                title.length > 48
                  ? "tp-display-font text-[clamp(1.7rem,1.3rem+1.4vw,2.6rem)] leading-[1.1]"
                  : "tp-display-font text-[clamp(2rem,1.4rem+2.2vw,3.4rem)] leading-[1.04]"
              }
            >
              {renderDisplayText(title)}
            </h2>
            <p className="mt-5 max-w-xl text-lg leading-relaxed text-primary-foreground/80">
              {subtitle}
            </p>

            <div className="mt-9 flex flex-col gap-4 sm:flex-row sm:items-center">
              <Button size="xl" className="group h-14 bg-background px-8 text-base text-foreground shadow-[var(--tp-shadow-md)] hover:bg-background/90" asChild>
                <Link to={buttonHref}>
                  {buttonText}
                  <span className="tp-btn-arrow bg-foreground/[0.08]"><ArrowRight /></span>
                </Link>
              </Button>
              {secondary}
            </div>

            {checklist.length > 0 && (
              <ul className="mt-10 flex flex-wrap gap-x-6 gap-y-3 border-t border-primary-foreground/15 pt-7 text-sm text-primary-foreground/85">
                {checklist.map((item) => (
                  <li key={item} className="flex items-center gap-2">
                    <span className="flex h-5 w-5 items-center justify-center rounded-full bg-primary-foreground/15">
                      <Check className="h-3 w-3" strokeWidth={3} />
                    </span>
                    {item}
                  </li>
                ))}
              </ul>
            )}
          </div>

          <div className="relative hidden lg:col-span-5 lg:block">
            <div className="tp-media absolute inset-y-8 left-0 right-8 shadow-2xl">
              <TenantImage src={image} alt="" loading="lazy" />
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
